import { cloneDeep, isEqual } from 'lodash';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDidMount } from '../../../../../hooks/life-cycle';
import { CustomerDetailActions } from '../../../../../redux/customer-detail/customer-detail.action';
import { DialogActions } from '../../../../../redux/dialog/dialog.action';
import { State } from '../../../../../redux/root.reducer';
import { MaintenanceSortState } from '../../../../../type/maintenance/maintenance.type';
import { DatePicker } from '../../../../ui/date-picker/date-picker';
import { Input } from '../../../../ui/input/input';
import { Select } from '../../../../ui/select/select';
import { RefindSP } from './refind.sp';
import { changeStore, sortEmployee } from '../../../../../utilities/sort-employee';
import { pulldown } from '../../../../../utilities/pulldown';
import { useAppSelector } from '../../../../../hooks/use-redux';
import { Project } from '../../../../../type/project/project.type';
import { DateFormatter as df } from '../../../../../utilities/date-formatter';
import { MasterActions } from '../../../../../redux/master/master.action';

type Props = {
  callback: (sort: MaintenanceSortState) => void;
  data?: Project | null;
}

export const RefindMaintenanceSP = (props:Props) => {
  const { callback, data } = props;

  /* Hook */
  const dispatch = useDispatch();
  const sort = useSelector((state: State) => state.customerDetail.maintenanceSort, isEqual);
  const {
    storeList,
    employeeList,
  } = useAppSelector((v) => v.master);

  const [sortState, setSortState] = useState(cloneDeep(sort));

  /* Callback */
  const handleClickSearch = useCallback(() => {
    dispatch(DialogActions.pop());
    callback(sortState);
  }, [callback, sort]);

  const setState = useCallback((v: MaintenanceSortState) => {
    setSortState(cloneDeep(v));
    dispatch(CustomerDetailActions.setMaintenanceSort(v));
  }, [dispatch]);

  useDidMount(() => {
    dispatch(MasterActions.api.store.getList({}));
    dispatch(MasterActions.api.employee.getList({}));
  });

  return (
    <RefindSP callback={handleClickSearch}>
      <>
        <div className="category_wrap">
          <div className="item_wrap">
            <div className="item_label">対応</div>
            <Select
              value={sortState.supported_kubun}
              defaultLabel="指定なし"
              onChange={(v) => setState({ ...sortState, supported_kubun: Number(v) })}
              options={[
                { text: '未対応', value: 1 },
                { text: '対応済み', value: 2 },
              ]}
            />
          </div>
          <div className="item_wrap">
            <div className="item_label">案件名</div>
            <Input
              value={!data ? sortState.project_name : data.name}
              onChange={(e) => {
                setState({ ...sortState, project_name: e.target.value });
              }}
              disabled={data !== undefined}
            />
          </div>
          <div className="item_wrap">
            <div className="item_label">案件担当店舗</div>
            <Select
              className=""
              value={!data ? sortState.project_store_id : data.project_store_id}
              defaultLabel="指定なし"
              disabled={data !== undefined}
              onChange={(v) => setState({
                ...sortState,
                ...changeStore({
                  employeeList,
                  storeId: Number(v),
                  storeKey: 'store_id',
                  returnStoreKey: 'project_store_id',
                  employeeKey: 'project_employee_id',
                }),
              })}
              options={pulldown(storeList)}
            />
          </div>
          <div className="item_wrap">
            <div className="item_label">案件担当者</div>
            <Select
              className=""
              value={!data ? sortState.project_employee_id : data.project_employee_id}
              defaultLabel="指定なし"
              disabled={data !== undefined}
              onChange={(v) => setState({ ...sortState, project_employee_id: Number(v) })}
              options={pulldown(
                sortEmployee({
                  employeeList,
                  storeId: sortState.project_store_id,
                  storeKey: 'store_id',
                }),
              )}
            />
          </div>
          <div className="item_wrap">
            <div className="item_label">着工日</div>
            <DatePicker
              disabled={data !== undefined}
              date={!data ? sortState.construction_date : df.str2date(data.construction_date)}
              onChange={(v) => setState(
                { ...sortState, construction_date: v },
              )}
            />
          </div>
          <div className="item_wrap">
            <div className="item_label">完工日</div>
            <DatePicker
              disabled={data !== undefined}
              date={!data ? sortState.completion_date || null : df.str2date(data.completion_date)}
              onChange={(v) => setState(
                { ...sortState, completion_date: v },
              )}
            />
          </div>
          <div className="item_wrap">
            <div className="item_label">メンテナンス日</div>
            <DatePicker
              date={sortState.maintenance_date || null}
              onChange={(v) => setState(
                { ...sortState, maintenance_date: v },
              )}
            />
          </div>
          <div className="item_wrap">
            <div className="item_label">タイトル</div>
            <Input
              className=""
              value={sort.title}
              onChange={(e) => setState({ ...sortState, title: e.target.value })}
            />
          </div>
          <div className="item_wrap">
            <div className="item_label">対応日</div>
            <DatePicker
              date={sortState.supported_date || null}
              onChange={(v) => setState(
                { ...sortState, supported_date: v },
              )}
            />
          </div>
        </div>
      </>
    </RefindSP>
  );
};
